import './ProducerContainer.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useGet } from 'restful-react';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import { useTranslation } from 'react-i18next';
import ProducerHeader from '../../ProjectHeaderBar/ProjectHeader';
import ProjectMainSection from '../../ProjectMainSection/ProjectMainSection';
import ProjectDetailsProvider from '../../../contexts/project/ProjectProvider';
import { OkResponse } from '../../../common/interfaces';
import { DefaultProject, Project, ProjectContext } from '../../../types/Project';
import { useParams } from 'react-router';
import ProjectControlPanel from '../../ProjectControlPanel/ProjectControlPanel';
import useAutosaveProject from '../../../hooks/useAutosaveProject';
import { UserTypeEnum } from '../../../types/enums/UserTypesEnum';
import useSignalRConnection from '../../../hooks/useSignalRConnection';

type RouterParams = {
  projectId: string;
};

const ProducerContainer = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { projectId } = useParams<RouterParams>();
  const path = useMemo(() => `/projects/${projectId}`, [projectId]);

  const [hubCreated, setHubCreated] = useState<boolean>(false);

  const context: ProjectContext = useAutosaveProject(DefaultProject);
  const {
    createHubConnection,
    isChatConnected,
    chatMessages,
    sendMessageToRoom,
    sendRequestControl,
    sendTakeControl,
    sendGiveControl,
    getPeerInfo,
    peerRequestingControl,
    connectedPeers,
    isAwaitingApiSaveToComplete,
    sendIsAwaitingApiSaveToComplete,
    isUserActive,
    isUserLive,
    myConnectionIsLive,
    sendProjectToAllUsers,
  } = useSignalRConnection(context);

  const { data: project, loading, error, refetch } = useGet({
    path: path,
    resolve: (project: OkResponse<Project>) => {
      const result = project.result;

      if (result.deletedUtc
        && result.deletedUtc !== '') {
          history.push('/');
          return;
        }

      const encoder = result?.stream?.encoders?.find(
        (x) => x.languageIsoCode === result?.stream?.sourceLanguageIsoCode,
      );

      if (encoder) {
        result.encoder = encoder;
      }

      context.setProject(result);

      if (!hubCreated) {
        createHubConnection(
          result?.id ?? '',
          result?.createdByUser?.id ?? '',
          `Producer-${result?.id}`,
          UserTypeEnum.Producer,
          result?.createdByUser?.name ?? '',
          result?.messages ?? [],
        );
        setHubCreated(true);
      }
      return result;
    },
  });

  // catch all changes to context and broadcast to others.
  useEffect(() => {
    if (context.project) {
      sendProjectToAllUsers(context.project);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.project]);

  useEffect(() => {
    sendIsAwaitingApiSaveToComplete(context.isAwaitingApiSaveToComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.isAwaitingApiSaveToComplete]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={t('error-messages.generic-error')} />;
  }

  if (!project) {
    return null;
  }

  const reloadProject = () => {
    refetch();
  };

  return (
    <ProjectDetailsProvider value={context}>
      <section>
        <ProducerHeader reloadProject={reloadProject} isProducer={true} />
        <div className="producer-container">
          <ProjectControlPanel
            chatMessages={chatMessages}
            userType={UserTypeEnum.Producer}
            token={`Producer-${context.project?.id}`}
            sendMessageToRoomCallback={sendMessageToRoom}
            isChatConnected={isChatConnected}
            sendProjectToAllUsers={sendProjectToAllUsers}
          />
          <ProjectMainSection
            isProducer={true}
            sendRequestControlCallback={sendRequestControl}
            sendTakeControlCallback={sendTakeControl}
            sendGiveControlCallback={sendGiveControl}
            getPeerInfoCallback={getPeerInfo}
            peerRequestingControl={peerRequestingControl}
            connectedPeers={connectedPeers}
            isAwaitingApiSaveToComplete={isAwaitingApiSaveToComplete}
            isUserActiveCallback={isUserActive}
            isUserLiveCallback={isUserLive}
            myConnectionIsLive={myConnectionIsLive}
          />
        </div>
      </section>
    </ProjectDetailsProvider>
  );
};

export default ProducerContainer;
